import React, { useState, useEffect, useRef } from "react";
import { X, Search, Sparkles, Smile, Film, Loader2 } from "lucide-react";
import { fetchTrendingGifs, searchGifs } from "../../services/giphyService";

const QUICK_REACTIONS = ["👍", "❤️", "😂", "🎉", "🔥", "🚀", "👀", "👏", "💯", "✅", "🙏", "💡", "✨", "🤝", "🎯"];

const EMOJI_CATEGORIES = [
  {
    name: "Smileys & Emotion",
    emojis: [
      { char: "😀", name: "grinning" }, { char: "😃", name: "smiley" }, { char: "😄", name: "smile" },
      { char: "😁", name: "beam" }, { char: "😆", name: "laughing" }, { char: "😅", name: "sweat smile" },
      { char: "😂", name: "joy" }, { char: "🤣", name: "rofl" }, { char: "🥲", name: "tear smile" },
      { char: "😊", name: "blush" }, { char: "😇", name: "angel" }, { char: "🙂", name: "slight smile" },
      { char: "😉", name: "wink" }, { char: "😌", name: "relieved" }, { char: "😍", name: "heart eyes" },
      { char: "🥰", name: "in love" }, { char: "😘", name: "kiss" }, { char: "😋", name: "yum" },
      { char: "😜", name: "winking tongue" }, { char: "🤪", name: "zany" }, { char: "😎", name: "cool" },
      { char: "🤓", name: "nerd" }, { char: "🧐", name: "monocle" }, { char: "🥳", name: "party" },
      { char: "😏", name: "smirk" }, { char: "😒", name: "unamused" }, { char: "🙄", name: "eye roll" },
      { char: "😬", name: "grimace" }, { char: "🤔", name: "thinking" }, { char: "🤐", name: "zipper" },
      { char: "😴", name: "sleep" }, { char: "🤯", name: "exploding head" }, { char: "🤠", name: "cowboy" }
    ]
  },
  {
    name: "Gestures & People",
    emojis: [
      { char: "👍", name: "thumbs up" }, { char: "👎", name: "thumbs down" }, { char: "👌", name: "ok" },
      { char: "✌️", name: "peace" }, { char: "🤞", name: "fingers crossed" }, { char: "🤟", name: "love you" },
      { char: "🤘", name: "rock on" }, { char: "🤙", name: "call me" }, { char: "👏", name: "clap" },
      { char: "🙌", name: "raised hands" }, { char: "👐", name: "open hands" }, { char: "🤝", name: "handshake" },
      { char: "🙏", name: "pray please" }, { char: "✍️", name: "writing" }, { char: "💪", name: "muscle" },
      { char: "👋", name: "wave" }, { char: "✋", name: "high five" }, { char: "👀", name: "eyes" },
      { char: "🧠", name: "brain" }, { char: "🙋", name: "raising hand" }
    ]
  },
  {
    name: "Reactions & Symbols",
    emojis: [
      { char: "🔥", name: "fire lit" }, { char: "💥", name: "boom explosion" }, { char: "✨", name: "sparkles" },
      { char: "🌟", name: "star" }, { char: "🚀", name: "rocket ship" }, { char: "💯", name: "100 percent" },
      { char: "🎉", name: "tada party" }, { char: "🎊", name: "confetti" }, { char: "🎯", name: "target bullseye" },
      { char: "🏆", name: "trophy" }, { char: "🥇", name: "first medal" }, { char: "💡", name: "idea bulb" },
      { char: "❤️", name: "red heart" }, { char: "💙", name: "blue heart" }, { char: "💜", name: "purple heart" },
      { char: "💚", name: "green heart" }, { char: "🤍", name: "white heart" }, { char: "💔", name: "broken heart" },
      { char: "✅", name: "check mark" }, { char: "❌", name: "cross mark" }, { char: "⚡", name: "lightning" }
    ]
  },
  {
    name: "Work & Logistics",
    emojis: [
      { char: "💻", name: "laptop computer" }, { char: "📱", name: "phone mobile" }, { char: "📦", name: "package box" },
      { char: "🚚", name: "truck delivery" }, { char: "✈️", name: "airplane transport" }, { char: "🚢", name: "ship cargo" },
      { char: "📋", name: "clipboard tasks" }, { char: "📊", name: "chart analytics" }, { char: "📈", name: "growth up" },
      { char: "📝", name: "memo document" }, { char: "🔒", name: "lock secure" }, { char: "🔑", name: "key" },
      { char: "⏰", name: "clock time" }, { char: "💼", name: "briefcase work" }
    ]
  }
];

const GIF_CATEGORIES = ["Trending", "Thumbs Up", "Applause", "Celebration", "Mind Blown", "Laughing", "Dance", "Work"];

export default function ChatEmojiGiphyPicker({ isOpen, onClose, onSelectEmoji, onSelectGif }) {
  const [activeTab, setActiveTab] = useState("emojis"); // 'emojis' | 'gifs'
  const [emojiSearch, setEmojiSearch] = useState("");
  
  // GIF state
  const [gifSearch, setGifSearch] = useState("");
  const [activeGifCategory, setActiveGifCategory] = useState("Trending");
  const [gifs, setGifs] = useState([]);
  const [loadingGifs, setLoadingGifs] = useState(false);

  const containerRef = useRef(null);

  // Close on click outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        onClose();
      }
    }
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Load GIFs when tab is 'gifs' or search changes
  useEffect(() => {
    if (!isOpen || activeTab !== "gifs") return;

    let isSubscribed = true;
    setLoadingGifs(true);

    const timer = setTimeout(async () => {
      let results = [];
      if (gifSearch.trim()) {
        results = await searchGifs(gifSearch);
      } else if (activeGifCategory === "Trending") {
        results = await fetchTrendingGifs();
      } else {
        results = await searchGifs(activeGifCategory);
      }

      if (isSubscribed) {
        setGifs(results);
        setLoadingGifs(false);
      }
    }, 250);

    return () => {
      isSubscribed = false;
      clearTimeout(timer);
    };
  }, [isOpen, activeTab, gifSearch, activeGifCategory]);

  if (!isOpen) return null;

  // Filter emojis based on search
  const filteredEmojiCategories = EMOJI_CATEGORIES.map(cat => {
    if (!emojiSearch.trim()) return cat;
    const q = emojiSearch.toLowerCase().trim();
    const matched = cat.emojis.filter(e => e.name.toLowerCase().includes(q) || e.char.includes(q));
    return { ...cat, emojis: matched };
  }).filter(cat => cat.emojis.length > 0);

  return (
    <div 
      ref={containerRef}
      className="glass-card animate-slide-up"
      style={{
        position: "absolute",
        bottom: "calc(100% + 8px)",
        right: 0,
        width: "360px",
        maxHeight: "440px",
        height: "440px",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "var(--bg-card)",
        border: "1px solid var(--border-color)",
        borderRadius: "10px",
        boxShadow: "0 12px 32px rgba(0, 0, 0, 0.45)",
        zIndex: 1100,
        overflow: "hidden"
      }}
    >
      {/* Top Header & Tabs */}
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0.5rem 0.75rem",
        borderBottom: "1px solid var(--border-color)",
        backgroundColor: "rgba(0,0,0,0.15)"
      }}>
        <div style={{ display: "flex", gap: "0.35rem" }}>
          <button
            type="button"
            onClick={() => setActiveTab("emojis")}
            style={{
              padding: "0.3rem 0.65rem",
              borderRadius: "6px",
              border: "none",
              backgroundColor: activeTab === "emojis" ? "var(--primary)" : "transparent",
              color: activeTab === "emojis" ? "#ffffff" : "var(--text-secondary)",
              fontSize: "0.75rem",
              fontWeight: 600,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "0.3rem",
              transition: "all 0.15s ease"
            }}
          >
            <Smile size={14} /> Emoticons
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("gifs")}
            style={{
              padding: "0.3rem 0.65rem",
              borderRadius: "6px",
              border: "none",
              backgroundColor: activeTab === "gifs" ? "var(--primary)" : "transparent",
              color: activeTab === "gifs" ? "#ffffff" : "var(--text-secondary)",
              fontSize: "0.75rem",
              fontWeight: 600,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "0.3rem",
              transition: "all 0.15s ease"
            }}
          >
            <Film size={14} /> GIPHY GIFs
          </button>
        </div>
        <button
          type="button"
          onClick={onClose}
          style={{
            background: "none",
            border: "none",
            color: "var(--text-secondary)",
            cursor: "pointer",
            padding: "0.2rem",
            display: "flex",
            alignItems: "center"
          }}
          title="Close Picker"
        >
          <X size={16} />
        </button>
      </div>

      {/* TAB 1: EMOTICONS */}
      {activeTab === "emojis" && (
        <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
          {/* Quick reactions bar */}
          <div style={{
            display: "flex",
            gap: "0.25rem",
            padding: "0.4rem 0.6rem",
            borderBottom: "1px solid rgba(255,255,255,0.04)",
            overflowX: "auto",
            backgroundColor: "rgba(255,255,255,0.02)"
          }}>
            {QUICK_REACTIONS.map((em, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => onSelectEmoji(em)}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "1.1rem",
                  cursor: "pointer",
                  padding: "0.15rem 0.25rem",
                  borderRadius: "4px"
                }}
                className="hover-bg-subtle"
                title={em}
              >
                {em}
              </button>
            ))}
          </div>

          {/* Search box */}
          <div style={{ padding: "0.5rem 0.75rem", position: "relative" }}>
            <Search size={13} style={{ position: "absolute", left: "1.1rem", top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)" }} />
            <input
              type="text"
              placeholder="Search emoticons..."
              value={emojiSearch}
              onChange={(e) => setEmojiSearch(e.target.value)}
              className="form-input"
              style={{
                width: "100%",
                paddingLeft: "2rem",
                height: "30px",
                fontSize: "0.75rem",
                borderRadius: "6px"
              }}
            />
            {emojiSearch && (
              <button
                type="button"
                onClick={() => setEmojiSearch("")}
                style={{ position: "absolute", right: "1.1rem", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer" }}
              >
                <X size={12} />
              </button>
            )}
          </div>

          {/* Emoji Grid Container */}
          <div style={{ flex: 1, overflowY: "auto", padding: "0 0.75rem 0.75rem 0.75rem" }}>
            {filteredEmojiCategories.map((cat, cIdx) => (
              <div key={cIdx} style={{ marginBottom: "0.6rem" }}>
                <div style={{ fontSize: "0.68rem", fontWeight: 700, color: "var(--text-secondary)", textTransform: "uppercase", marginBottom: "0.3rem", letterSpacing: "0.5px" }}>
                  {cat.name}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(8, 1fr)", gap: "0.2rem" }}>
                  {cat.emojis.map((e, eIdx) => (
                    <button
                      key={eIdx}
                      type="button"
                      onClick={() => onSelectEmoji(e.char)}
                      style={{
                        background: "none",
                        border: "none",
                        fontSize: "1.25rem",
                        cursor: "pointer",
                        padding: "0.3rem 0",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "6px",
                        transition: "transform 0.1s ease"
                      }}
                      className="hover-bg-subtle"
                      title={e.name}
                    >
                      {e.char}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            {filteredEmojiCategories.length === 0 && (
              <div style={{ padding: "2rem 0", textAlign: "center", color: "var(--text-muted)", fontSize: "0.75rem" }}>
                No emoticons match "{emojiSearch}"
              </div>
            )}
          </div>
        </div>
      )}

      {/* TAB 2: GIPHY GIFS */}
      {activeTab === "gifs" && (
        <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
          {/* GIF Search box */}
          <div style={{ padding: "0.5rem 0.75rem 0.3rem 0.75rem", position: "relative" }}>
            <Search size={13} style={{ position: "absolute", left: "1.1rem", top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)" }} />
            <input
              type="text"
              placeholder="Search GIPHY..."
              value={gifSearch}
              onChange={(e) => {
                setGifSearch(e.target.value);
                if (e.target.value) setActiveGifCategory("");
              }}
              className="form-input"
              style={{
                width: "100%",
                paddingLeft: "2rem",
                height: "30px",
                fontSize: "0.75rem",
                borderRadius: "6px"
              }}
            />
            {gifSearch && (
              <button
                type="button"
                onClick={() => setGifSearch("")}
                style={{ position: "absolute", right: "1.1rem", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer" }}
              >
                <X size={12} />
              </button>
            )}
          </div>

          {/* Quick Categories Chips */}
          <div style={{
            display: "flex",
            gap: "0.3rem",
            padding: "0.2rem 0.75rem 0.4rem 0.75rem",
            overflowX: "auto",
            scrollbarWidth: "none"
          }}>
            {GIF_CATEGORIES.map((cat, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => {
                  setGifSearch("");
                  setActiveGifCategory(cat);
                }}
                style={{
                  padding: "0.2rem 0.5rem",
                  borderRadius: "12px",
                  border: "1px solid var(--border-color)",
                  backgroundColor: activeGifCategory === cat && !gifSearch ? "var(--primary-light)" : "rgba(255,255,255,0.03)",
                  color: activeGifCategory === cat && !gifSearch ? "var(--primary)" : "var(--text-secondary)",
                  fontSize: "0.68rem",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  cursor: "pointer"
                }}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* GIPHY Stream Grid */}
          <div style={{ flex: 1, overflowY: "auto", padding: "0 0.75rem 0.75rem 0.75rem" }}>
            {loadingGifs ? (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "3rem 0", color: "var(--text-secondary)", gap: "0.5rem" }}>
                <Loader2 size={24} className="spin" style={{ color: "var(--primary)" }} />
                <span style={{ fontSize: "0.75rem" }}>Loading GIFs...</span>
              </div>
            ) : gifs.length === 0 ? (
              <div style={{ padding: "2.5rem 0", textAlign: "center", color: "var(--text-muted)", fontSize: "0.75rem" }}>
                No GIFs found. Try another search.
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "0.4rem" }}>
                {gifs.map((g) => (
                  <div
                    key={g.id}
                    onClick={() => onSelectGif(g)}
                    style={{
                      position: "relative",
                      borderRadius: "6px",
                      overflow: "hidden",
                      backgroundColor: "rgba(0,0,0,0.2)",
                      cursor: "pointer",
                      height: "95px",
                      border: "1px solid rgba(255,255,255,0.05)",
                      transition: "transform 0.15s ease, border-color 0.15s ease"
                    }}
                    className="gif-card-hover"
                    title={g.title}
                  >
                    <img
                      src={g.previewUrl || g.url}
                      alt={g.title}
                      loading="lazy"
                      onError={(e) => {
                        if (g.url && e.currentTarget.src !== g.url) {
                          e.currentTarget.src = g.url;
                        }
                      }}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block"
                      }}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* GIPHY Attribution Footer */}
          <div style={{
            padding: "0.25rem 0.75rem",
            fontSize: "0.62rem",
            color: "var(--text-muted)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderTop: "1px solid rgba(255,255,255,0.04)",
            backgroundColor: "rgba(0,0,0,0.15)"
          }}>
            <span>Powered by <strong>GIPHY</strong></span>
            <span>Click any GIF to send</span>
          </div>
        </div>
      )}
    </div>
  );
}
